import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import {
  ThemeProvider,
  createTheme,
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import DrawIcon from "@mui/icons-material/Draw";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { ensureFormUuid } from "../../utils/formUuid";

const THEME_COLOR = "#8b1634";

const sf005DateLabelStyle = {
  fontWeight: 600,
  color: THEME_COLOR,
  fontSize: "14px",
  marginBottom: "6px",
  display: "block",
};

const sf005DateInputStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "14px",
  width: "100%",
  boxSizing: "border-box",
};

const sf005DateFocusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = THEME_COLOR;
    e.target.style.boxShadow = "0 0 0 2px rgba(139, 22, 52, 0.15)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  },
};

const muiTheme = createTheme({
  palette: {
    primary: { main: THEME_COLOR, dark: "#6d1128", light: "#a83252" },
    background: { default: "#eef2f6", paper: "#ffffff" },
    text: { primary: "#0f172a", secondary: "#64748b" },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
    h1: { fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.02em" },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 600 },
    body2: { fontSize: "0.8125rem", lineHeight: 1.5 },
    caption: { fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.06em" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          "&:before": { display: "none" },
          boxShadow: "none",
          border: "1px solid #e2e8f0",
          borderRadius: "12px !important",
          overflow: "hidden",
          "&.Mui-expanded": { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
          px: 2,
          "&.Mui-expanded": { minHeight: 48 },
        },
        content: { my: 1 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
      },
    },
  },
});

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(d) {
  if (!d) return "—";
  const x = new Date(d);
  return isNaN(x.getTime()) ? "—" : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function toDataUrl(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

function getDirectorSignatureFromPad(pad) {
  if (!pad || pad.isEmpty()) return "";
  return pad.getCanvas().toDataURL("image/png");
}

function addedByLabel(r) {
  if (r.addedByFirstName || r.addedByLastName) {
    return `${r.addedByFirstName || ""} ${r.addedByLastName || ""}`.trim() || "—";
  }
  return r.userId ? String(r.userId) : "—";
}

const READONLY_SIGNATURES = [
  { key: "supervisorSignature", label: "Supervisor" },
  { key: "employeeSignature", label: "Employee" },
  { key: "clientRepSignature", label: "Client representative" },
];

const SECTION_ORDER = ["initial", "refusal", "employee", "clientRep", "ttsl", "resolution"];
const SECTION_LABELS = {
  initial: "Incident details",
  refusal: "Refusal to work",
  employee: "Employee",
  clientRep: "Client representative",
  ttsl: "TTSL SMT actions",
  resolution: "Resolution",
};

const READONLY_FIELDS = [
  { key: "complainantName", label: "Complainant", section: "initial", span: 4 },
  { key: "dateOfRefusal", label: "Date", type: "date", section: "initial", span: 2 },
  { key: "timeOfRefusal", label: "Time", section: "initial", span: 2 },
  { key: "siteLocation", label: "Site / location", section: "initial", span: 4 },
  { key: "supervisorName", label: "Supervisor", section: "initial", span: 4 },
  { key: "detailsOfConcernsRaised", label: "Concerns raised", textarea: true, section: "refusal", span: 12 },
  { key: "detailsOfActionsTakenBySupervisor", label: "Supervisor actions", textarea: true, section: "refusal", span: 12 },
  { key: "employeeNameInCapitals", label: "Name (capitals)", section: "employee", span: 4 },
  { key: "employeePosition", label: "Position", section: "employee", span: 4 },
  { key: "employeeClientCompany", label: "Client company", section: "employee", span: 4 },
  { key: "clientRepNameInCapitals", label: "Name (capitals)", section: "clientRep", span: 4 },
  { key: "clientRepPosition", label: "Position", section: "clientRep", span: 4 },
  { key: "clientRepClientCompany", label: "Client company", section: "clientRep", span: 4 },
  { key: "actionsTakenByTTSLSMT", label: "TTSL SMT actions", textarea: true, section: "ttsl", span: 12 },
  { key: "dateResolvedClosedOut", label: "Date closed out", type: "date", section: "resolution", span: 4 },
];

const SIG_PAD_H = 112;
const DIRECTOR_SIGN_PAGE_CLASS = "sf005-director-sign-view";

function readOnlyDisplay(record, field) {
  const v = record[field.key];
  if (field.type === "date") return formatDate(v);
  if (v == null || String(v).trim() === "") return "—";
  return String(v);
}

function FieldCell({ label, value, span = 4, multiline }) {
  const empty = value === "—";
  return (
    <Grid item xs={12} sm={span >= 8 ? 12 : 6} md={span}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box
        sx={{
          color: empty ? "text.disabled" : "text.primary",
          bgcolor: empty ? "grey.50" : "#fafbfc",
          border: "1px solid",
          borderColor: empty ? "divider" : alpha(THEME_COLOR, 0.12),
          borderRadius: 1.5,
          px: 1.25,
          py: multiline ? 1 : 0.75,
          minHeight: multiline ? 56 : 36,
          maxHeight: multiline ? 140 : "none",
          overflow: multiline ? "auto" : "hidden",
          fontSize: "0.8125rem",
          lineHeight: 1.5,
          whiteSpace: multiline ? "pre-wrap" : "nowrap",
          textOverflow: multiline ? "clip" : "ellipsis",
        }}
      >
        {value}
      </Box>
    </Grid>
  );
}

function RefusalToWorkFormSignaturePage() {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [directorProfile, setDirectorProfile] = useState(null);
  const [sigWidth, setSigWidth] = useState(320);
  const sigDirectorRef = useRef(null);
  const sigShellRef = useRef(null);
  const sigWidthLockedRef = useRef(false);

  useEffect(() => {
    document.documentElement.classList.add(DIRECTOR_SIGN_PAGE_CLASS);
    return () => document.documentElement.classList.remove(DIRECTOR_SIGN_PAGE_CLASS);
  }, []);

  useEffect(() => {
    const el = sigShellRef.current;
    if (!el || typeof ResizeObserver === "undefined" || sigWidthLockedRef.current) return;
    const ro = new ResizeObserver((entries) => {
      if (sigWidthLockedRef.current) return;
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setSigWidth(Math.floor(Math.min(Math.max(w - 32, 260), 560)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [record]);

  const fetchRecord = useCallback(async () => {
    if (!id) return;
    setLoadError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getBaseUrl()}/api/refusal-to-work-form/record/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && data.data) {
        sigWidthLockedRef.current = false;
        setHydrated(false);
        setRecord(data.data);
      } else {
        setRecord(null);
        setLoadError(data.message || "Could not load this form.");
      }
    } catch {
      setRecord(null);
      setLoadError("Could not load this form.");
    }
  }, [id]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  useEffect(() => {
    async function loadDirectorProfile() {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${getBaseUrl()}/api/directors`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data) && data.data.length > 0) {
          setDirectorProfile(data.data[0]);
        }
      } catch {
        setDirectorProfile(null);
      }
    }
    loadDirectorProfile();
  }, []);

  useEffect(() => {
    if (!record || hydrated) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      const pad = sigDirectorRef.current;
      if (!pad) {
        sigWidthLockedRef.current = true;
        setHydrated(true);
        return;
      }
      const raw = record.directorSignature;
      pad.clear();
      if (raw) {
        try {
          pad.fromDataURL(toDataUrl(raw));
        } catch {
          pad.clear();
        }
      }
      sigWidthLockedRef.current = true;
      setHydrated(true);
    }, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [record, hydrated, sigWidth]);

  const saveDirectorFromCanvas = () => {
    const dataUrl = getDirectorSignatureFromPad(sigDirectorRef.current);
    if (dataUrl) {
      setRecord((prev) => (prev ? { ...prev, directorSignature: dataUrl } : prev));
    }
  };

  const clearDirectorSignature = () => {
    sigWidthLockedRef.current = false;
    if (sigDirectorRef.current) sigDirectorRef.current.clear();
    setRecord((prev) => (prev ? { ...prev, directorSignature: "" } : prev));
  };

  const handleSave = async () => {
    if (!record?._id) return;
    const pad = sigDirectorRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error("Signature of TTSL Director is required. Please sign before saving.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const directorSignature =
        getDirectorSignatureFromPad(sigDirectorRef.current) || record.directorSignature || "";
      const res = await fetch(`${getBaseUrl()}/api/refusal-to-work-form/${record._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          formUuid: ensureFormUuid(record.formUuid),
          actionsTakenByTTSLSMT: record.actionsTakenByTTSLSMT || "",
          dateResolvedClosedOut: record.dateResolvedClosedOut || "",
          directorSignature,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Director signature saved.");
        if (data.data) {
          setRecord({ ...data.data, directorSignature: directorSignature || data.data.directorSignature });
        }
      } else {
        toast.error(data.message || "Failed to save.");
      }
    } catch {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const renderSectionFields = (sectionKey) => {
    const fields = READONLY_FIELDS.filter((f) => f.section === sectionKey);
    if (!fields.length && sectionKey !== "initial") return null;

    return (
      <Grid container spacing={1.5}>
        {sectionKey === "initial" && <FieldCell label="Added by" value={addedByLabel(record)} span={4} />}
        {fields.map((field) => (
          <FieldCell
            key={field.key}
            label={field.label}
            value={readOnlyDisplay(record, field)}
            span={field.span || 4}
            multiline={field.textarea}
          />
        ))}
      </Grid>
    );
  };

  const pageContent = () => {
    if (loadError) {
      return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {loadError}
          </Alert>
          <Button component={RouterLink} to="/admin/refusal-to-work-form" startIcon={<ArrowBackIcon />} variant="outlined">
            Back to list
          </Button>
        </Container>
      );
    }

    if (!record) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={36} sx={{ color: THEME_COLOR }} />
            <Typography variant="body2" color="text.secondary">
              Loading form…
            </Typography>
          </Stack>
        </Box>
      );
    }

    const summaryChips = [
      { label: "Complainant", value: record.complainantName },
      { label: "Site", value: record.siteLocation },
      { label: "Date", value: formatDate(record.dateOfRefusal) },
    ].filter((c) => c.value);

    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: { xs: 10, sm: 4 } }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: THEME_COLOR,
            backgroundImage: `linear-gradient(135deg, ${THEME_COLOR} 0%, #6d1128 100%)`,
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: { xs: 52, sm: 56 }, px: { xs: 1.5, sm: 2 } }}>
            <Typography variant="caption" sx={{ color: alpha("#fff", 0.85), fontWeight: 700, mr: 1.5 }}>
              SF-005
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#fff", flexGrow: 1 }}>
              Director signature
            </Typography>
            <Button
              component={RouterLink}
              to="/admin/refusal-to-work-form"
              size="small"
              sx={{
                color: "#fff",
                borderColor: alpha("#fff", 0.4),
                mr: 1,
                display: { xs: "none", sm: "inline-flex" },
              }}
              variant="outlined"
              startIcon={<ArrowBackIcon fontSize="small" />}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={saving}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon fontSize="small" />}
              sx={{
                bgcolor: "#fff",
                color: THEME_COLOR,
                "&:hover": { bgcolor: alpha("#fff", 0.92) },
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(180deg, ${alpha(THEME_COLOR, 0.04)} 0%, #fff 100%)`,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 560 }}>
              Draw your signature in the box below, then tap <strong>Save</strong>. Expand form sections only if you need
              to review details.
            </Typography>
            {summaryChips.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {summaryChips.map((c) => (
                  <Chip
                    key={c.label}
                    size="small"
                    label={`${c.label}: ${c.value}`}
                    sx={{
                      bgcolor: alpha(THEME_COLOR, 0.08),
                      color: THEME_COLOR,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      maxWidth: "100%",
                      height: "auto",
                      py: 0.5,
                      "& .MuiChip-label": { whiteSpace: "normal" },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2,
              border: "2px solid",
              borderColor: alpha(THEME_COLOR, 0.35),
              boxShadow: `0 8px 24px ${alpha(THEME_COLOR, 0.08)}`,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: alpha(THEME_COLOR, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DrawIcon sx={{ color: THEME_COLOR, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" color="text.primary">
                    Your signature
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Step 1 — required
                  </Typography>
                </Box>
              </Stack>
              <Chip label="Sign here" color="primary" size="small" sx={{ fontWeight: 700 }} />
            </Stack>

            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} lg={7}>
                <Box ref={sigShellRef} className="sf005-dir-sig-shell">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25, color: "text.primary" }}>
                    Details of ACTIONS taken by TTSL SMT (i.e. proposed Safer working method, negotiations with the Clients
                    Site Manager or removal of the Hazard.)
                  </Typography>
                  <Box
                    component="textarea"
                    value={record.actionsTakenByTTSLSMT || ""}
                    onChange={(e) =>
                      setRecord((prev) => (prev ? { ...prev, actionsTakenByTTSLSMT: e.target.value } : prev))
                    }
                    sx={{
                      width: "100%",
                      minHeight: 140,
                      border: "2px solid",
                      borderColor: alpha(THEME_COLOR, 0.8),
                      borderRadius: 2,
                      px: 1.5,
                      py: 1.25,
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      resize: "vertical",
                      outline: "none",
                      mb: 1.75,
                      "&:focus": { borderColor: THEME_COLOR, boxShadow: `0 0 0 2px ${alpha(THEME_COLOR, 0.12)}` },
                    }}
                  />
                  <Box component="label" style={sf005DateLabelStyle}>
                    Date issue resolved / Closed Out:
                  </Box>
                  <Box sx={{ width: "100%", mb: 2 }}>
                    <DatePicker
                      selected={
                        record.dateResolvedClosedOut ? new Date(record.dateResolvedClosedOut) : null
                      }
                      onChange={(d) =>
                        setRecord((prev) =>
                          prev ? { ...prev, dateResolvedClosedOut: d ? d.toISOString().slice(0, 10) : "" } : prev
                        )
                      }
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      style={sf005DateInputStyle}
                      placeholderText="Select date"
                      wrapperClassName="w-100"
                      {...sf005DateFocusHandlers}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1.25, color: "text.primary" }}>
                    Signature of TTSL Director
                  </Typography>
                  <Box className="sf005-dir-sig-frame">
                    <Box className="sf005-dir-sig-canvas-wrap">
                      <SignatureCanvas
                        ref={sigDirectorRef}
                        penColor={THEME_COLOR}
                        backgroundColor="transparent"
                        clearOnResize={false}
                        velocityFilterWeight={0.7}
                        minWidth={1}
                        maxWidth={2.5}
                        canvasProps={{
                          width: sigWidth,
                          height: SIG_PAD_H,
                          className: "sf005-dir-signature-canvas",
                          style: {
                            width: `${sigWidth}px`,
                            height: `${SIG_PAD_H}px`,
                            border: `1px solid ${alpha(THEME_COLOR, 0.2)}`,
                            borderRadius: 10,
                            background: "#fff",
                            touchAction: "none",
                          },
                        }}
                        onEnd={saveDirectorFromCanvas}
                      />
                    </Box>
                    <Box className="sf005-dir-sig-toolbar">
                      <span>Use mouse or finger to sign</span>
                      <button type="button" className="sf005-dir-btn-clear" onClick={clearDirectorSignature}>
                        Clear signature
                      </button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Box className="sf005-dir-meta" sx={{ height: "100%" }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PersonOutlineIcon sx={{ fontSize: 18, color: THEME_COLOR }} />
                    <Typography className="sf005-dir-meta__title" sx={{ mb: "0 !important" }}>
                      Director details
                    </Typography>
                  </Stack>
                  {[
                    ["Name", directorProfile?.name],
                    ["Role", "TTSL Director"],
                    ["Email", directorProfile?.email],
                    ["Phone", directorProfile?.phoneNumber],
                  ].map(([label, val]) => (
                    <Box key={label} className="sf005-dir-meta__row">
                      <span className="sf005-dir-meta__label">{label}</span>
                      <span className="sf005-dir-meta__value">{val || "—"}</span>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Stack spacing={1.5}>
            <Accordion defaultExpanded={false} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "grey.50" }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: THEME_COLOR, mr: 1 }} />
                <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Review full form
                </Typography>
                <Chip label="Read-only" size="small" variant="outlined" sx={{ height: 22, fontSize: "0.65rem", mr: 1 }} />
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                {SECTION_ORDER.map((sectionKey, idx) => {
                  const fields = READONLY_FIELDS.filter((f) => f.section === sectionKey);
                  if (!fields.length && sectionKey !== "initial") return null;
                  return (
                    <Box key={sectionKey} sx={{ pt: idx === 0 ? 1 : 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 1,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          color: THEME_COLOR,
                          letterSpacing: "0.05em",
                        }}
                      >
                        {SECTION_LABELS[sectionKey]}
                      </Typography>
                      {renderSectionFields(sectionKey)}
                      {idx < SECTION_ORDER.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded={false} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "grey.50" }}>
                <DrawIcon sx={{ fontSize: 18, color: "text.secondary", mr: 1 }} />
                <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Other signatures on file
                </Typography>
                <Chip label="Read-only" size="small" variant="outlined" sx={{ height: 22, fontSize: "0.65rem", mr: 1 }} />
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2 }}>
                <Grid container spacing={2}>
                  {READONLY_SIGNATURES.map(({ key, label }) => {
                    const sigVal = record[key];
                    return (
                      <Grid item xs={12} sm={4} key={key}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.75 }}>
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 1,
                          }}
                        >
                          {sigVal ? (
                            <Box
                              component="img"
                              src={toDataUrl(sigVal)}
                              alt=""
                              sx={{ maxWidth: "100%", maxHeight: 72, objectFit: "contain" }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              No signature
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Container>

        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            py: 1.25,
            px: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: { xs: "flex", sm: "none" },
            gap: 1,
            zIndex: 1100,
            bgcolor: "#fff",
          }}
        >
          <Button component={RouterLink} to="/admin/refusal-to-work-form" size="medium" variant="outlined" fullWidth>
            Back
          </Button>
          <Button
            variant="contained"
            size="medium"
            disabled={saving}
            onClick={handleSave}
            fullWidth
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
          >
            Save signature
          </Button>
        </Paper>
      </Box>
    );
  };

  return <ThemeProvider theme={muiTheme}>{pageContent()}</ThemeProvider>;
}

export default RefusalToWorkFormSignaturePage;
